import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Users, Download, Globe, Target, TrendingUp, FileCheck, ArrowLeft, Search, Building2, Calendar, Filter, Shield, FileText, Eye, MessageSquare, Star, MousePointerClick, X } from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, PieChart, Pie, Cell, LineChart, Line, AreaChart, Area } from 'recharts';
import { useTranslation } from 'react-i18next';
import LevelUpHeader from '../components/LevelUpHeader';
import SunsetBanner from '../components/SunsetBanner';
import LevelUpFooter from '../components/LevelUpFooter';

const COLORS = ['#1B5E5F', '#2A7A7B', '#3D9A9B', '#5CBABA', '#7DD4D4', '#0C6A1D', '#2E8B57', '#4CAF50'];

// Helper: Get fiscal year date range (FY starts July, ends June)
// FY 2026 = July 2025 - June 2026 (Microsoft FY convention)
const getFiscalYearRange = (fyYear) => {
  // FY year N starts on July 1 of year (N-1) and ends on June 30 of year N
  const fyStartYear = fyYear - 1;
  return {
    start: `${fyStartYear}-07-01`,
    end: `${fyYear}-06-30`
  };
};

// Get current FY based on today's date
const getCurrentFY = () => {
  const today = new Date();
  const month = today.getMonth() + 1; // 1-12
  const year = today.getFullYear();
  // If month is July or later, we're in next FY
  return month >= 7 ? year + 1 : year;
};

const formatLocalDate = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

// Most recently completed full week (Monday - Sunday)
const getLastCompletedWeekRange = () => {
  const today = new Date();
  const dayOfWeek = today.getDay(); // 0=Sun...6=Sat
  const daysSinceMonday = dayOfWeek === 0 ? 6 : dayOfWeek - 1;
  const thisMonday = new Date(today.getFullYear(), today.getMonth(), today.getDate() - daysSinceMonday);
  const lastSunday = new Date(thisMonday.getFullYear(), thisMonday.getMonth(), thisMonday.getDate() - 1);
  const lastMonday = new Date(lastSunday.getFullYear(), lastSunday.getMonth(), lastSunday.getDate() - 6);
  return { start: formatLocalDate(lastMonday), end: formatLocalDate(lastSunday) };
};

// Most recently completed full calendar month (1st - last day)
const getLastCompletedMonthRange = () => {
  const today = new Date();
  const firstOfThisMonth = new Date(today.getFullYear(), today.getMonth(), 1);
  const lastOfPrevMonth = new Date(firstOfThisMonth.getFullYear(), firstOfThisMonth.getMonth(), 0);
  const firstOfPrevMonth = new Date(lastOfPrevMonth.getFullYear(), lastOfPrevMonth.getMonth(), 1);
  return { start: formatLocalDate(firstOfPrevMonth), end: formatLocalDate(lastOfPrevMonth) };
};

// Timeframe Filter Component
const TimeframeFilter = ({ currentTimeframe, onTimeframeChange, tabId, useCalendarRange = false }) => {
  const currentFY = getCurrentFY();
  const availableFYs = [currentFY, currentFY - 1, currentFY - 2, currentFY - 3];
  const todayStr = new Date().toISOString().split('T')[0];
  const [showCustomPicker, setShowCustomPicker] = useState(false);
  const [customStart, setCustomStart] = useState(currentTimeframe.preset === 'Custom' && currentTimeframe.custom ? currentTimeframe.custom.start : '');
  const [customEnd, setCustomEnd] = useState(currentTimeframe.preset === 'Custom' && currentTimeframe.custom ? currentTimeframe.custom.end : '');
  const [customError, setCustomError] = useState('');

  const handlePresetClick = (preset) => {
    if (preset === 'All') {
      onTimeframeChange(tabId, 'All', null, null, null);
    } else if (preset === 'Last Week') {
      if (useCalendarRange) {
        onTimeframeChange(tabId, 'Last Week', null, getLastCompletedWeekRange(), null);
      } else {
        onTimeframeChange(tabId, 'Last Week', 7, null, null);
      }
    } else if (preset === 'Last Month') {
      if (useCalendarRange) {
        onTimeframeChange(tabId, 'Last Month', null, getLastCompletedMonthRange(), null);
      } else {
        onTimeframeChange(tabId, 'Last Month', 30, null, null);
      }
    }
    setShowCustomPicker(false);
  };

  const handleFYChange = (e) => {
    const fyYear = Number(e.target.value);
    if (!fyYear) {
      // "Select FY" placeholder - do nothing
      return;
    }
    const range = getFiscalYearRange(fyYear);
    onTimeframeChange(tabId, `FY ${fyYear}`, null, { start: range.start, end: range.end }, fyYear);
    setShowCustomPicker(false);
  };

  const handleApplyCustomRange = () => {
    if (!customStart || !customEnd) {
      setCustomError('Please pick both a start and end date');
      return;
    }
    if (customStart > customEnd) {
      setCustomError('Start date must be before end date');
      return;
    }
    setCustomError('');
    onTimeframeChange(tabId, 'Custom', null, { start: customStart, end: customEnd }, null);
    setShowCustomPicker(false);
  };

  const isFilterActive = currentTimeframe.preset !== 'All' || showCustomPicker;

  const handleClearFilters = () => {
    setCustomStart('');
    setCustomEnd('');
    setCustomError('');
    setShowCustomPicker(false);
    onTimeframeChange(tabId, 'All', null, null, null);
  };

  return (
    <div style={{ marginBottom: '24px', padding: '16px 20px', backgroundColor: '#F9FAFB', borderRadius: '8px', border: '1px solid #E5E7EB' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
        <span style={{ fontSize: '14px', fontWeight: '600', color: '#374151' }}>Time Range:</span>
        
        {/* Preset Buttons */}
        <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
          {['All', 'Last Week', 'Last Month'].map((label) => (
            <button
              key={label}
              data-testid={`timeframe-preset-${tabId}-${label.replace(/\s+/g, '-').toLowerCase()}`}
              onClick={() => handlePresetClick(label)}
              style={{
                padding: '8px 16px',
                border: currentTimeframe.preset === label ? 'none' : '1px solid #D1D5DB',
                backgroundColor: currentTimeframe.preset === label ? '#1c4b50' : '#FFFFFF',
                color: currentTimeframe.preset === label ? '#FFFFFF' : '#374151',
                borderRadius: '6px',
                fontSize: '13px',
                fontWeight: '600',
                cursor: 'pointer',
                transition: 'all 0.2s'
              }}
            >
              {label}
            </button>
          ))}
        </div>

        {/* Vertical Divider */}
        <div style={{ width: '1px', height: '24px', backgroundColor: '#D1D5DB', margin: '0 4px' }} />

        {/* FY Year Dropdown */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <label style={{ fontSize: '13px', color: '#6B7280', fontWeight: '500' }}>Fiscal Year:</label>
          <select
            value={currentTimeframe.fyYear || ''}
            onChange={handleFYChange}
            style={{
              padding: '8px 12px',
              border: currentTimeframe.fyYear ? '2px solid #1c4b50' : '1px solid #D1D5DB',
              borderRadius: '6px',
              fontSize: '13px',
              fontWeight: '600',
              color: currentTimeframe.fyYear ? '#1c4b50' : '#374151',
              backgroundColor: '#FFFFFF',
              cursor: 'pointer',
              minWidth: '120px'
            }}
          >
            <option value="">Select FY</option>
            {availableFYs.map(fy => (
              <option key={fy} value={fy}>FY {fy}</option>
            ))}
          </select>
        </div>

        {/* Vertical Divider */}
        <div style={{ width: '1px', height: '24px', backgroundColor: '#D1D5DB', margin: '0 4px' }} />

        {/* Custom Range Button */}
        <button
          data-testid={`timeframe-preset-${tabId}-custom`}
          onClick={() => setShowCustomPicker(prev => !prev)}
          style={{
            padding: '8px 16px',
            border: currentTimeframe.preset === 'Custom' ? 'none' : '1px solid #D1D5DB',
            backgroundColor: currentTimeframe.preset === 'Custom' ? '#1c4b50' : '#FFFFFF',
            color: currentTimeframe.preset === 'Custom' ? '#FFFFFF' : '#374151',
            borderRadius: '6px',
            fontSize: '13px',
            fontWeight: '600',
            cursor: 'pointer',
            transition: 'all 0.2s',
            whiteSpace: 'nowrap'
          }}
        >
          {currentTimeframe.preset === 'Custom' && currentTimeframe.custom
            ? `Custom: ${currentTimeframe.custom.start} → ${currentTimeframe.custom.end}`
            : 'Custom Range'}
        </button>

        {/* Inline Custom Date Picker */}
        {showCustomPicker && (
          <div data-testid={`custom-range-picker-${tabId}`} style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
            <input
              type="date"
              data-testid={`custom-range-start-${tabId}`}
              value={customStart}
              max={customEnd || todayStr}
              onChange={(e) => { setCustomStart(e.target.value); setCustomError(''); }}
              style={{ padding: '7px 10px', border: '1px solid #D1D5DB', borderRadius: '6px', fontSize: '13px' }}
            />
            <span style={{ fontSize: '13px', color: '#6B7280' }}>→</span>
            <input
              type="date"
              data-testid={`custom-range-end-${tabId}`}
              value={customEnd}
              min={customStart || undefined}
              max={todayStr}
              onChange={(e) => { setCustomEnd(e.target.value); setCustomError(''); }}
              style={{ padding: '7px 10px', border: '1px solid #D1D5DB', borderRadius: '6px', fontSize: '13px' }}
            />
            <button
              data-testid={`custom-range-apply-${tabId}`}
              onClick={handleApplyCustomRange}
              style={{ padding: '8px 16px', backgroundColor: '#0C6A1D', color: '#FFFFFF', border: 'none', borderRadius: '6px', fontSize: '13px', fontWeight: '600', cursor: 'pointer', whiteSpace: 'nowrap' }}
            >
              Apply
            </button>
            {customError && <span data-testid={`custom-range-error-${tabId}`} style={{ fontSize: '12px', color: '#DC2626' }}>{customError}</span>}
          </div>
        )}

        {/* Clear Filters */}
        {isFilterActive && (
          <button
            data-testid={`timeframe-clear-${tabId}`}
            onClick={handleClearFilters}
            title="Clear filters"
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '4px',
              padding: '8px 12px',
              border: '1px solid #D1D5DB',
              backgroundColor: '#FFFFFF',
              color: '#6B7280',
              borderRadius: '6px',
              fontSize: '13px',
              fontWeight: '600',
              cursor: 'pointer',
              whiteSpace: 'nowrap'
            }}
          >
            <X size={14} />
            Clear
          </button>
        )}
      </div>
    </div>
  );
};

const ContentTypeTooltip = ({ active, payload, label }) => {
  if (!active || !payload || !payload.length) return null;
  const isWebUrl = label === 'Web URL';
  return (
    <div style={{ background: 'white', border: '1px solid #E0E0E0', borderRadius: '6px', padding: '10px 14px', fontSize: '13px' }}>
      <p style={{ fontWeight: '600', marginBottom: '6px', color: '#2C3E50' }}>{label}</p>
      {payload.map(p => (
        <p key={p.dataKey} style={{ color: p.color, margin: '2px 0' }}>
          {p.dataKey === 'assets' ? 'Total Assets' : isWebUrl ? 'Total Clicks (Web URL)' : 'Total Downloads'} : {p.value}
        </p>
      ))}
    </div>
  );
};

function Analytics({ user, onLogout }) {
  const navigate = useNavigate();
  const { t } = useTranslation();

  // Check user roles
  const isAdmin = user?.roles?.includes('admin');
  const isReportViewer = user?.roles?.includes('report_viewer');
  const isContentOwner = user?.roles?.includes('content_owner');

  // Content Owner ONLY (not Admin or Report Viewer) sees restricted view
  const isContentOwnerOnly = isContentOwner && !isAdmin && !isReportViewer;

  // Admin, Content Owner and Report Viewer all get calendar-based Last Week/Last Month ranges
  const useCalendarRange = isAdmin || isContentOwner || isReportViewer;
  const defaultLastMonthTimeframe = useCalendarRange
    ? { preset: 'Last Month', days: null, custom: getLastCompletedMonthRange(), fyYear: null }
    : { preset: 'Last Month', days: 30, custom: null, fyYear: null };

  const [overview, setOverview] = useState({});
  const [domainData, setDomainData] = useState([]);
  const [solutionData, setSolutionData] = useState([]);
  const [countryData, setCountryData] = useState([]);
  const [audienceData, setAudienceData] = useState([]);
  const [topAssets, setTopAssets] = useState([]);
  const [timeseriesData, setTimeseriesData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  
  // Detailed content analytics state
  const [downloadsBySolution, setDownloadsBySolution] = useState([]);
  const [downloadsByPartner, setDownloadsByPartner] = useState([]);
  const [contentUploads, setContentUploads] = useState({});
  const [downloadTrends, setDownloadTrends] = useState([]);
  const [weburlTimeseries, setWeburlTimeseries] = useState([]);
  
  // Content Owner specific state
  const [myContentStats, setMyContentStats] = useState(null);
  const [myContentTimeframe, setMyContentTimeframe] = useState({ preset: 'All', days: null, custom: null, fyYear: null });
  
  // Timeframe filter state (per tab)
  const [overviewTimeframe, setOverviewTimeframe] = useState({ preset: 'All', days: null, custom: null, fyYear: null });
  const [contentTimeframe, setContentTimeframe] = useState(defaultLastMonthTimeframe);
  const [downloadsTimeframe, setDownloadsTimeframe] = useState(defaultLastMonthTimeframe);
  const [partnersTimeframe, setPartnersTimeframe] = useState(defaultLastMonthTimeframe);
  const [feedbackTimeframe, setFeedbackTimeframe] = useState(defaultLastMonthTimeframe);
  const [ndaTimeframe, setNdaTimeframe] = useState(defaultLastMonthTimeframe);
  
  // NDA state (Admin only)
  const [ndaSummary, setNdaSummary] = useState({ by_domain: [], by_country: [], by_version: [] });
  const [ndaLookupEmail, setNdaLookupEmail] = useState('');
  const [ndaLookupResult, setNdaLookupResult] = useState(null);
  const [ndaLookupLoading, setNdaLookupLoading] = useState(false);
  const [ndaLookupError, setNdaLookupError] = useState('');
  
  // Feedback analytics state
  const [feedbackAnalytics, setFeedbackAnalytics] = useState(null);

  useEffect(() => {
    fetchAnalytics();
  }, []);

  // Fetch tab-specific data when switching tabs
  useEffect(() => {
    // Content owners only use the my-content tab — skip all other tab fetching
    if (isContentOwnerOnly) return;
    if (activeTab === 'overview') {
      fetchOverviewAnalytics();
    } else if (activeTab === 'content') {
      fetchContentAnalytics();
    } else if (activeTab === 'downloads') {
      fetchDownloadAnalytics();
    } else if (activeTab === 'partners') {
      fetchPartnerAnalytics();
    } else if (activeTab === 'feedback') {
      fetchFeedbackAnalytics();
    } else if (activeTab === 'nda' && isAdmin) {
      fetchNdaAnalytics();
    }
  }, [activeTab]);

  const fetchAnalytics = async () => {
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };

      // Content Owner Only - fetch only their own content stats
      if (isContentOwnerOnly) {
        await fetchMyContentStats(myContentTimeframe);
        setLoading(false);
        return;
      }
      // Base analytics - available to both Admin and Report Viewer
      const basePromises = [
        fetch(`${process.env.REACT_APP_BACKEND_URL}/api/analytics/by-domain?limit=10`, { headers }),
        fetch(`${process.env.REACT_APP_BACKEND_URL}/api/analytics/by-solution-area`, { headers }),
        fetch(`${process.env.REACT_APP_BACKEND_URL}/api/analytics/by-country?limit=10`, { headers }),
        fetch(`${process.env.REACT_APP_BACKEND_URL}/api/analytics/by-audience`, { headers }),
        fetch(`${process.env.REACT_APP_BACKEND_URL}/api/analytics/top-assets?limit=5`, { headers })
      ];

      const responses = await Promise.all(basePromises);
      const [domainRes, solutionRes, countryRes, audienceRes, topAssetsRes] = responses;

      const [
        domainDataRes, solutionDataRes, countryDataRes, 
        audienceDataRes, topAssetsRes2
      ] = await Promise.all(responses.map(r => r.json()));

      setDomainData((domainDataRes.data || []).map(d => ({ name: d.domain || d._id || 'Unknown', downloads: d.count || d.download_count || 0, clicks: d.weburl_clicks || 0 })));
      setSolutionData((solutionDataRes.data || []).map(d => ({ name: d._id || 'Unknown', downloads: d.count || d.total_downloads || 0, assets: d.asset_count || 0 })));
      setCountryData((countryDataRes.data || []).map(d => ({ name: d._id || 'Unknown', downloads: d.count || d.download_count || 0 })));
      setAudienceData((audienceDataRes.data || []).map(d => ({ name: d._id || 'Unknown', downloads: d.count || d.total_downloads || 0, assets: d.asset_count || 0 })));
      setTopAssets(topAssetsRes2.assets || topAssetsRes2.data || []);

      // NDA data - Admin only
      if (isAdmin) {
        try {
          const ndaRes = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/analytics/nda-summary`, { headers });
          if (ndaRes.ok) {
            const ndaData = await ndaRes.json();
            setNdaSummary(ndaData || { by_domain: [], by_country: [], by_version: [] });
          }
        } catch (e) {
          console.log('NDA summary not available');
        }
      }
      
      // Feedback analytics
      try {
        const fbRes = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/analytics/feedback-summary`, { headers });
        if (fbRes.ok) {
          setFeedbackAnalytics(await fbRes.json());
        }
      } catch (e) {
        console.log('Feedback analytics not available');
      }
    } catch (error) {
      console.error('Analytics fetch error:', error);
      toast.error('Failed to load analytics');
    } finally {
      setLoading(false);
    }
  };

  const lookupNdaByEmail = async () => {
    if (!ndaLookupEmail.trim()) {
      setNdaLookupError('Please enter an email address');
      return;
    }
    
    setNdaLookupLoading(true);
    setNdaLookupError('');
    setNdaLookupResult(null);
    
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/analytics/nda-lookup?email=${encodeURIComponent(ndaLookupEmail.trim())}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      const data = await response.json();
      
      if (response.ok) {
        setNdaLookupResult(data);
      } else {
        setNdaLookupError(data.detail || 'User not found');
      }
    } catch (error) {
      setNdaLookupError('Failed to lookup user');
    } finally {
      setNdaLookupLoading(false);
    }
  };

  // Helper functions for timeframe management
  const buildTimeframeParams = (timeframe) => {
    if (timeframe.custom) {
      return `start_date=${timeframe.custom.start}&end_date=${timeframe.custom.end}`;
    }
    if (timeframe.days) {
      return `days=${timeframe.days}`;
    }
    return ''; // For "All" time, no params
  };

  const getTimeframeLabel = (timeframe) => {
    if (timeframe.preset === 'All') return 'All Time';
    if (timeframe.preset === 'Last Week') return 'Last Week';
    if (timeframe.preset === 'Last Month') return 'Last Month';
    if (timeframe.preset === 'Custom' && timeframe.custom) {
      return `${timeframe.custom.start} → ${timeframe.custom.end}`;
    }
    if (timeframe.fyYear) {
      return `FY ${timeframe.fyYear}`;
    }
    return 'All Time';
  };

  const handleTimeframeChange = async (tab, preset, days = null, custom = null, fyYear = null) => {
    const newTimeframe = { preset, days, custom, fyYear };
    console.log(`Timeframe changed for ${tab}:`, newTimeframe);
    
    // Update the appropriate timeframe state
    switch(tab) {
      case 'overview':
        setOverviewTimeframe(newTimeframe);
        await fetchOverviewAnalytics(newTimeframe);
        break;
      case 'content':
        setContentTimeframe(newTimeframe);
        console.log('Calling fetchContentAnalytics with:', newTimeframe);
        await fetchContentAnalytics(newTimeframe);
        break;
      case 'downloads':
        setDownloadsTimeframe(newTimeframe);
        console.log('Calling fetchDownloadAnalytics with:', newTimeframe);
        await fetchDownloadAnalytics(newTimeframe);
        break;
      case 'partners':
        setPartnersTimeframe(newTimeframe);
        await fetchPartnerAnalytics(newTimeframe);
        break;
      case 'feedback':
        setFeedbackTimeframe(newTimeframe);
        await fetchFeedbackAnalytics(newTimeframe);
        break;
      case 'nda':
        setNdaTimeframe(newTimeframe);
        await fetchNdaAnalytics(newTimeframe);
        break;
      case 'my_content':
        setMyContentTimeframe(newTimeframe);
        await fetchMyContentStats(newTimeframe);
        break;
    }
  };

  // Tab-specific data fetching functions
  const fetchOverviewAnalytics = async (timeframe = overviewTimeframe) => {
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };
      
      // Build params for timeframe (empty for All Time)
      let params = '';
      if (timeframe.days) {
        params = `?days=${timeframe.days}`;
      } else if (timeframe.custom) {
        params = `?start_date=${timeframe.custom.start}&end_date=${timeframe.custom.end}`;
      }
      
      // For top assets, we need to append to existing query params
      const topAssetsParams = params ? `?limit=5&${params.substring(1)}` : '?limit=5';
      
      const [overviewRes, timeseriesRes, topAssetsRes, weburlRes] = await Promise.all([
        fetch(`${process.env.REACT_APP_BACKEND_URL}/api/analytics/overview${params}`, { headers }),
        fetch(`${process.env.REACT_APP_BACKEND_URL}/api/analytics/timeseries${params}`, { headers }),
        fetch(`${process.env.REACT_APP_BACKEND_URL}/api/analytics/top-assets${topAssetsParams}`, { headers }),
        fetch(`${process.env.REACT_APP_BACKEND_URL}/api/analytics/weburl-timeseries${params}`, { headers })
      ]);
      
      const [overviewData, timeseriesData, topAssetsData, weburlData] = await Promise.all([
        overviewRes.json(), 
        timeseriesRes.json(), 
        topAssetsRes.json(),
        weburlRes.json()
      ]);
      
      setOverview(overviewData);
      setTimeseriesData((timeseriesData.data || []).map(d => ({ date: d.date || d._id, downloads: d.count || 0 })));
      setTopAssets(topAssetsData.assets || topAssetsData.data || []);
      setWeburlTimeseries((weburlData.data || []).map(d => ({ date: d.date, clicks: d.clicks || 0 })));
    } catch (error) {
      console.error('Overview analytics error:', error);
      toast.error('Failed to load overview analytics');
    }
  };

  // Tab-specific data fetching functions
  const fetchContentAnalytics = async (timeframe = contentTimeframe) => {
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };
      const params = buildTimeframeParams(timeframe);
      const queryString = params ? `?${params}` : '';
      console.log('fetchContentAnalytics called with params:', params, 'queryString:', queryString);
      
      const [solRes, uploadsRes] = await Promise.all([
        fetch(`${process.env.REACT_APP_BACKEND_URL}/api/analytics/downloads-by-solution${queryString}`, { headers }),
        fetch(`${process.env.REACT_APP_BACKEND_URL}/api/analytics/content-uploads${queryString}`, { headers })
      ]);
      
      const [solData, uploadsData] = await Promise.all([solRes.json(), uploadsRes.json()]);
      console.log('Content analytics data received:', solData);
      console.log('Setting downloadsBySolution with:', solData.data);
      setDownloadsBySolution(solData.data || []);
      setContentUploads(uploadsData || {});
      
      // Force re-render by updating a timestamp
      console.log('Content data set. Downloads by solution length:', solData.data?.length);
    } catch (error) {
      console.error('Content analytics error:', error);
      toast.error('Failed to load content analytics');
    }
  };

  const fetchDownloadAnalytics = async (timeframe = downloadsTimeframe) => {
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };
      const params = buildTimeframeParams(timeframe);
      console.log('fetchDownloadAnalytics called with params:', params);
      
      const trendsQuery = params ? `?${params}&group_by=day` : '?group_by=day';
      const topAssetsQuery = params ? `?limit=10&${params}` : '?limit=10';
      const countryQuery = params ? `?limit=10&${params}` : '?limit=10';
      const queryString = params ? `?${params}` : '';
      
      console.log('Download trends URL:', `${process.env.REACT_APP_BACKEND_URL}/api/analytics/download-trends${trendsQuery}`);
      
      const [trendsRes, topRes, countryRes, solRes] = await Promise.all([
        fetch(`${process.env.REACT_APP_BACKEND_URL}/api/analytics/download-trends${trendsQuery}`, { headers }),
        fetch(`${process.env.REACT_APP_BACKEND_URL}/api/analytics/top-assets${topAssetsQuery}`, { headers }),
        fetch(`${process.env.REACT_APP_BACKEND_URL}/api/analytics/by-country${countryQuery}`, { headers }),
        fetch(`${process.env.REACT_APP_BACKEND_URL}/api/analytics/downloads-by-solution${queryString}`, { headers })
      ]);
      
      const [trendsData, topData, countryData, solData] = await Promise.all([
        trendsRes.json(), topRes.json(), countryRes.json(), solRes.json()
      ]);
      console.log('Download analytics data received - trends:', trendsData.data?.length, 'top assets:', topData.assets?.length, 'countries:', countryData.data?.length, 'solutions:', solData.data?.length);
      
      setDownloadTrends(trendsData.data || []);
      setTopAssets(topData.assets || topData.data || []);
      setCountryData((countryData.data || []).map(d => ({ name: d._id || 'Unknown', downloads: d.count || d.download_count || 0 })));
      setDownloadsBySolution(solData.data || []);
    } catch (error) {
      console.error('Download analytics error:', error);
      toast.error('Failed to load download analytics');
    }
  };

  const fetchPartnerAnalytics = async (timeframe = partnersTimeframe) => {
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };
      const params = buildTimeframeParams(timeframe);
      const queryString = params ? `?${params}` : '';
      
      const [partnerRes, domainRes, countryRes] = await Promise.all([
        fetch(`${process.env.REACT_APP_BACKEND_URL}/api/analytics/downloads-by-partner-domain?limit=15${params ? '&' + params : ''}`, { headers }),
        fetch(`${process.env.REACT_APP_BACKEND_URL}/api/analytics/by-domain?limit=10${params ? '&' + params : ''}`, { headers }),
        fetch(`${process.env.REACT_APP_BACKEND_URL}/api/analytics/by-country?limit=10${params ? '&' + params : ''}`, { headers })
      ]);
      
      const [partnerData, domainData, countryData] = await Promise.all([partnerRes.json(), domainRes.json(), countryRes.json()]);
      setDownloadsByPartner(partnerData.data || []);
      setDomainData((domainData.data || []).map(d => ({ name: d.domain || d._id || 'Unknown', downloads: d.count || d.download_count || 0, clicks: d.weburl_clicks || 0 })));
      setCountryData((countryData.data || []).map(d => ({ name: d._id || 'Unknown', downloads: d.count || d.download_count || 0 })));
    } catch (error) {
      toast.error('Failed to load partner analytics');
    }
  };

  const fetchMyContentStats = async (timeframe = myContentTimeframe) => {
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };
      const params = buildTimeframeParams(timeframe);
      const queryString = params ? `?${params}` : '';
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/analytics/my-content-stats${queryString}`, { headers });
      if (res.ok) {
        setMyContentStats(await res.json());
      } else {
        toast.error('Failed to load your content statistics');
      }
    } catch (error) {
      toast.error('Failed to load your content statistics');
    }
  };

  const fetchFeedbackAnalytics = async (timeframe = feedbackTimeframe) => {
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };
      const params = buildTimeframeParams(timeframe);
      const queryString = params ? `?${params}` : '';
      
      const fbRes = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/analytics/feedback-summary${queryString}`, { headers });
      if (fbRes.ok) {
        setFeedbackAnalytics(await fbRes.json());
      }
    } catch (error) {
      toast.error('Failed to load feedback analytics');
    }
  };

  const fetchNdaAnalytics = async (timeframe = ndaTimeframe) => {
    if (!isAdmin) return;
    
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };
      const params = buildTimeframeParams(timeframe);
      const queryString = params ? `?${params}` : '';
      
      const ndaRes = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/analytics/nda-summary${queryString}`, { headers });
      if (ndaRes.ok) {
        setNdaSummary(await ndaRes.json());
      }
    } catch (error) {
      toast.error('Failed to load NDA analytics');
    }
  };

  // Define available tabs based on role
  const getTabs = () => {
    // Content Owner Only sees just their content
    if (isContentOwnerOnly) {
      return [{ id: 'my-content', label: 'My Content Stats' }];
    }
    
    const baseTabs = [
      { id: 'overview', label: t('analytics.overview') },
      { id: 'content', label: t('analytics.contentAnalytics') },
      { id: 'downloads', label: t('analytics.downloadAnalytics') },
      { id: 'partners', label: t('analytics.partnerAnalytics') },
      { id: 'feedback', label: 'Feedback' }
    ];
    
    // Admin-only tabs
    if (isAdmin) {
      baseTabs.push({ id: 'nda', label: t('analytics.ndaTracking') });
    }
    
    return baseTabs;
  };

  if (loading) {
    return (
      <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <span>Loading analytics...</span>
      </div>
    );
  }

  // Content Owner Only View - Restricted to their own content stats
  if (isContentOwnerOnly) {
    return (
      <div style={{ minHeight: '100vh', backgroundColor: '#FAFAFA', display: 'flex', flexDirection: 'column' }}>
        <LevelUpHeader user={user} onLogout={onLogout} />
        <SunsetBanner user={user} />
        <div style={{ flex: 1 }}>
          <nav style={{ backgroundColor: '#1B5E5F', borderTop: '1px solid rgba(255,255,255,0.1)', padding: '12px 0' }}>
            <div style={{ maxWidth: '1400px', margin: '0 auto', padding: '0 24px', display: 'flex', gap: '32px' }}>
              <button onClick={() => navigate('/browse')} style={{ color: 'rgba(255,255,255,0.7)', background: 'none', border: 'none', display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', fontSize: '14px' }}>
                <ArrowLeft size={16} /> Back to Why Microsoft – Compete Enablement for Partners
              </button>
            </div>
          </nav>

          <div style={{ maxWidth: '1400px', margin: '0 auto', padding: '32px 24px' }}>
            <div style={{ marginBottom: '24px' }}>
              <h1 style={{ fontSize: '28px', fontWeight: '700', color: '#2C3E50', marginBottom: '8px' }}>
                My Content Analytics
              </h1>
              <p style={{ color: '#5A6C7D', fontSize: '14px' }}>
                Download and view statistics for your uploaded content
              </p>
            </div>

            <TimeframeFilter
              currentTimeframe={myContentTimeframe}
              onTimeframeChange={handleTimeframeChange}
              tabId="my_content"
              useCalendarRange={useCalendarRange}
            />

            {/* My Content Stats Cards */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '20px', marginBottom: '32px' }}>
              <div style={{ backgroundColor: 'white', borderRadius: '12px', padding: '24px', boxShadow: '0 2px 8px rgba(0,0,0,0.06)' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '12px' }}>
                  <div style={{ backgroundColor: '#E8F5E9', padding: '10px', borderRadius: '8px' }}>
                    <FileText size={20} color="#1B5E5F" />
                  </div>
                  <span style={{ color: '#5A6C7D', fontSize: '14px' }}>My Assets</span>
                </div>
                <div style={{ fontSize: '32px', fontWeight: '700', color: '#2C3E50' }}>
                  {myContentStats?.total_assets || 0}
                </div>
              </div>

              <div style={{ backgroundColor: 'white', borderRadius: '12px', padding: '24px', boxShadow: '0 2px 8px rgba(0,0,0,0.06)' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '12px' }}>
                  <div style={{ backgroundColor: '#E3F2FD', padding: '10px', borderRadius: '8px' }}>
                    <Download size={20} color="#1976D2" />
                  </div>
                  <span style={{ color: '#5A6C7D', fontSize: '14px' }}>Total Downloads</span>
                </div>
                <div style={{ fontSize: '32px', fontWeight: '700', color: '#1976D2' }}>
                  {myContentStats?.total_downloads || 0}
                </div>
              </div>

              <div style={{ backgroundColor: 'white', borderRadius: '12px', padding: '24px', boxShadow: '0 2px 8px rgba(0,0,0,0.06)' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '12px' }}>
                  <div style={{ backgroundColor: '#FFF3E0', padding: '10px', borderRadius: '8px' }}>
                    <Eye size={20} color="#E65100" />
                  </div>
                  <span style={{ color: '#5A6C7D', fontSize: '14px' }}>Total Clicks (Web URL)</span>
                </div>
                <div style={{ fontSize: '32px', fontWeight: '700', color: '#E65100' }}>
                  {myContentStats?.total_clicks || 0}
                </div>
              </div>
            </div>

            {/* Downloads by Asset */}
            {myContentStats?.downloads_by_asset?.length > 0 && (
              <div style={{ backgroundColor: 'white', borderRadius: '12px', padding: '24px', boxShadow: '0 2px 8px rgba(0,0,0,0.06)', marginBottom: '24px' }}>
                <h3 style={{ fontSize: '16px', fontWeight: '600', color: '#2C3E50', marginBottom: '16px' }}>
                  Downloads by Asset
                </h3>
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={myContentStats.downloads_by_asset}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="title" tick={{ fontSize: 12 }} angle={-45} textAnchor="end" height={80} />
                    <YAxis />
                    <Tooltip />
                    <Bar dataKey="downloads" fill="#1B5E5F" />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            )}

            {/* Recent Downloads */}
            {myContentStats?.recent_downloads?.length > 0 && (
              <div style={{ backgroundColor: 'white', borderRadius: '12px', padding: '24px', boxShadow: '0 2px 8px rgba(0,0,0,0.06)' }}>
                <h3 style={{ fontSize: '16px', fontWeight: '600', color: '#2C3E50', marginBottom: '16px' }}>
                  Recent Downloads (Last 30 Days)
                </h3>
                <div style={{ overflowX: 'auto' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                    <thead>
                      <tr style={{ backgroundColor: '#F9FAFB' }}>
                        <th style={{ padding: '12px 16px', textAlign: 'left', fontSize: '12px', fontWeight: '600', color: '#5A6C7D' }}>ASSET</th>
                        <th style={{ padding: '12px 16px', textAlign: 'left', fontSize: '12px', fontWeight: '600', color: '#5A6C7D' }}>ORGANIZATION DOMAIN</th>
                        <th style={{ padding: '12px 16px', textAlign: 'left', fontSize: '12px', fontWeight: '600', color: '#5A6C7D' }}>DATE</th>
                      </tr>
                    </thead>
                    <tbody>
                      {myContentStats.recent_downloads.map((dl, idx) => (
                        <tr key={idx} style={{ borderTop: '1px solid #E5E7EB' }}>
                          <td style={{ padding: '12px 16px', fontSize: '14px', color: '#2C3E50' }}>{dl.asset_title}</td>
                          <td style={{ padding: '12px 16px', fontSize: '14px', color: '#5A6C7D' }}>{dl.org_domain}</td>
                          <td style={{ padding: '12px 16px', fontSize: '13px', color: '#9CA3AF' }}>
                            {dl.timestamp ? new Date(dl.timestamp).toLocaleDateString() : '-'}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {myContentStats?.total_assets === 0 && (
              <div style={{ backgroundColor: 'white', borderRadius: '12px', padding: '48px', textAlign: 'center', boxShadow: '0 2px 8px rgba(0,0,0,0.06)' }}>
                <FileText size={48} color="#9CA3AF" style={{ marginBottom: '16px' }} />
                <h3 style={{ fontSize: '18px', fontWeight: '600', color: '#2C3E50', marginBottom: '8px' }}>No Content Uploaded Yet</h3>
                <p style={{ color: '#5A6C7D', marginBottom: '16px' }}>Upload your first content asset to start tracking downloads.</p>
                <button
                  onClick={() => navigate('/upload')}
                  style={{ padding: '12px 24px', backgroundColor: '#1B5E5F', color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer', fontWeight: '600' }}
                >
                  Upload Content
                </button>
              </div>
            )}
          </div>
        </div>
        <LevelUpFooter />
      </div>
    );
  }

  return (
    <div style={{ minHeight: '100vh', backgroundColor: '#FAFAFA', display: 'flex', flexDirection: 'column' }}>
      {/* LevelUp Header */}
      <LevelUpHeader user={user} onLogout={onLogout} />
      <SunsetBanner user={user} />

      <div style={{ flex: 1 }}>

      {/* Navigation */}
      <nav style={{ backgroundColor: '#1B5E5F', borderTop: '1px solid rgba(255,255,255,0.1)', padding: '12px 0' }}>
        <div style={{ maxWidth: '1400px', margin: '0 auto', padding: '0 24px', display: 'flex', gap: '32px' }}>
          <button onClick={() => navigate('/browse')} style={{ color: 'rgba(255,255,255,0.7)', background: 'none', border: 'none', display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', fontSize: '14px' }}>
            <ArrowLeft size={16} /> Back to Why Microsoft – Compete Enablement for Partners
          </button>
        </div>
      </nav>

      {/* Main Content */}
      <div style={{ maxWidth: '1400px', margin: '0 auto', padding: '32px 24px' }}>
        {/* Page Title */}
        <div style={{ marginBottom: '24px' }}>
          <h1 style={{ fontSize: '28px', fontWeight: '700', color: '#2C3E50', marginBottom: '8px' }}>
            Analytics Dashboard
          </h1>
          <p style={{ color: '#5A6C7D', fontSize: '14px' }}>
            {isAdmin ? t('analytics.subtitle') : t('analytics.subtitle')}
          </p>
        </div>

        {/* Tabs */}
        <div style={{ display: 'flex', gap: '4px', marginBottom: '24px', borderBottom: '2px solid #E0E0E0' }}>
          {getTabs().map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              style={{
                padding: '12px 20px',
                border: 'none',
                background: 'none',
                fontSize: '14px',
                fontWeight: '600',
                color: activeTab === tab.id ? '#1B5E5F' : '#5A6C7D',
                cursor: 'pointer',
                borderBottom: activeTab === tab.id ? '3px solid #1B5E5F' : '3px solid transparent',
                marginBottom: '-2px'
              }}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {/* Overview Tab */}
        {activeTab === 'overview' && (
          <div>
            <TimeframeFilter 
              currentTimeframe={overviewTimeframe}
              onTimeframeChange={handleTimeframeChange}
              tabId="overview"
              useCalendarRange={useCalendarRange}
            />
            
            {/* Stats Cards */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '16px', marginBottom: '32px' }}>
              <div style={{ backgroundColor: 'white', borderRadius: '8px', padding: '20px', boxShadow: '0 1px 3px rgba(0,0,0,0.06)' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '8px' }}>
                  <FileCheck size={24} color="#1B5E5F" />
                  <span style={{ fontSize: '14px', color: '#5A6C7D' }}>Total Assets</span>
                </div>
                <span style={{ fontSize: '32px', fontWeight: '700', color: '#2C3E50' }}>{overview.total_assets || 0}</span>
              </div>
              <div style={{ backgroundColor: 'white', borderRadius: '8px', padding: '20px', boxShadow: '0 1px 3px rgba(0,0,0,0.06)' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '8px' }}>
                  <Download size={24} color="#0C6A1D" />
                  <span style={{ fontSize: '14px', color: '#5A6C7D' }}>Total Downloads</span>
                </div>
                <span style={{ fontSize: '32px', fontWeight: '700', color: '#0C6A1D' }}>{overview.total_downloads || 0}</span>
              </div>
              <div style={{ backgroundColor: 'white', borderRadius: '8px', padding: '20px', boxShadow: '0 1px 3px rgba(0,0,0,0.06)' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '8px' }}>
                  <MousePointerClick size={24} color="#1B5E5F" />
                  <span style={{ fontSize: '14px', color: '#5A6C7D' }}>Web URL Engagement</span>
                </div>
                <span style={{ fontSize: '32px', fontWeight: '700', color: '#1B5E5F' }}>{overview.web_url_total_clicks || 0}</span>
              </div>
              <div style={{ backgroundColor: 'white', borderRadius: '8px', padding: '20px', boxShadow: '0 1px 3px rgba(0,0,0,0.06)' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '8px' }}>
                  <Users size={24} color="#2A7A7B" />
                  <span style={{ fontSize: '14px', color: '#5A6C7D' }}>Total Users</span>
                </div>
                <span style={{ fontSize: '32px', fontWeight: '700', color: '#2C3E50' }}>{overview.total_users || 0}</span>
              </div>
              <div style={{ backgroundColor: 'white', borderRadius: '8px', padding: '20px', boxShadow: '0 1px 3px rgba(0,0,0,0.06)' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '8px' }}>
                  <TrendingUp size={24} color="#E67E22" />
                  <span style={{ fontSize: '14px', color: '#5A6C7D' }}>Active Partners</span>
                </div>
                <span style={{ fontSize: '32px', fontWeight: '700', color: '#2C3E50' }}>{overview.active_partners || overview.active_users || 0}</span>
              </div>
            </div>

            {/* Download Trend Chart */}
            <div style={{ backgroundColor: 'white', borderRadius: '8px', padding: '24px', boxShadow: '0 1px 3px rgba(0,0,0,0.06)', marginBottom: '24px' }}>
              <h3 style={{ fontSize: '18px', fontWeight: '600', color: '#2C3E50', marginBottom: '16px' }}>
                Download Activity ({getTimeframeLabel(overviewTimeframe)})
              </h3>
              <ResponsiveContainer width="100%" height={320}>
                <AreaChart data={timeseriesData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E0E0E0" />
                  <XAxis dataKey="date" tick={{ fontSize: 11 }} interval="preserveStartEnd" label={{ value: 'Date', position: 'insideBottom', offset: -5, fontSize: 12, fill: '#5A6C7D' }} />
                  <YAxis tick={{ fontSize: 12 }} label={{ value: 'Downloads', angle: -90, position: 'insideLeft', fontSize: 12, fill: '#5A6C7D' }} />
                  <Tooltip formatter={(value) => [value, 'Downloads']} labelFormatter={(label) => `Date: ${label}`} />
                  <Legend wrapperStyle={{ paddingTop: '10px' }} />
                  <Area type="monotone" dataKey="downloads" stroke="#1B5E5F" fill="#B2DFDB" name="Downloads" />
                </AreaChart>
              </ResponsiveContainer>
            </div>

            {/* Web URL Click Activity Chart */}
            <div style={{ backgroundColor: 'white', borderRadius: '8px', padding: '24px', boxShadow: '0 1px 3px rgba(0,0,0,0.06)', marginBottom: '24px' }}>
              <h3 style={{ fontSize: '18px', fontWeight: '600', color: '#2C3E50', marginBottom: '16px' }}>
                Click Activity ({getTimeframeLabel(overviewTimeframe)})
              </h3>
              <ResponsiveContainer width="100%" height={280}>
                <AreaChart data={weburlTimeseries}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E0E0E0" />
                  <XAxis dataKey="date" tick={{ fontSize: 11 }} interval="preserveStartEnd" label={{ value: 'Date', position: 'insideBottom', offset: -5, fontSize: 12, fill: '#5A6C7D' }} />
                  <YAxis tick={{ fontSize: 12 }} label={{ value: 'Clicks', angle: -90, position: 'insideLeft', fontSize: 12, fill: '#5A6C7D' }} />
                  <Tooltip formatter={(value) => [value, 'Clicks']} labelFormatter={(label) => `Date: ${label}`} />
                  <Legend wrapperStyle={{ paddingTop: '10px' }} />
                  <Area type="monotone" dataKey="clicks" stroke="#1B5E5F" fill="#CCFBF1" name="Web URL Clicks" />
                </AreaChart>
              </ResponsiveContainer>
              {weburlTimeseries.length === 0 && (
                <div style={{ textAlign: 'center', color: '#9CA3AF', fontSize: '14px', padding: '40px 0' }}>No Web URL click data for this period</div>
              )}
            </div>

            {/* Top Assets */}
            <div style={{ backgroundColor: 'white', borderRadius: '8px', padding: '24px', boxShadow: '0 1px 3px rgba(0,0,0,0.06)' }}>
              <h3 style={{ fontSize: '18px', fontWeight: '600', color: '#2C3E50', marginBottom: '16px' }}>
                Top Downloaded Assets ({getTimeframeLabel(overviewTimeframe)})
              </h3>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead style={{ backgroundColor: '#F5F5F5' }}>
                  <tr>
                    <th style={{ padding: '12px 16px', textAlign: 'left', fontSize: '12px', fontWeight: '600', color: '#5A6C7D' }}>Asset Title</th>
                    <th style={{ padding: '12px 16px', textAlign: 'left', fontSize: '12px', fontWeight: '600', color: '#5A6C7D' }}>Solution Area</th>
                    <th style={{ padding: '12px 16px', textAlign: 'right', fontSize: '12px', fontWeight: '600', color: '#5A6C7D' }}>Downloads</th>
                  </tr>
                </thead>
                <tbody>
                  {topAssets.map((asset, idx) => (
                    <tr key={idx} style={{ borderTop: '1px solid #E0E0E0' }}>
                      <td style={{ padding: '12px 16px', fontSize: '14px', fontWeight: '500', color: '#2C3E50' }}>{asset.title}</td>
                      <td style={{ padding: '12px 16px', fontSize: '14px', color: '#5A6C7D' }}>{asset.solution_area}</td>
                      <td style={{ padding: '12px 16px', fontSize: '14px', fontWeight: '600', color: '#1B5E5F', textAlign: 'right' }}>{asset.download_count || 0}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Content Analytics Tab */}
        {activeTab === 'content' && (
          <div>
            <TimeframeFilter 
              currentTimeframe={contentTimeframe}
              onTimeframeChange={handleTimeframeChange}
              tabId="content"
              useCalendarRange={useCalendarRange}
            />
            <div style={{ display: 'grid', gap: '24px' }}>
            {/* Content by Solution Area */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '24px' }}>
              <div style={{ backgroundColor: 'white', borderRadius: '8px', padding: '24px', boxShadow: '0 1px 3px rgba(0,0,0,0.06)' }}>
                <h3 style={{ fontSize: '16px', fontWeight: '600', color: '#2C3E50', marginBottom: '16px' }}>Content by Solution Area</h3>
                <ResponsiveContainer width="100%" height={280}>
                  <PieChart key={contentTimeframe.preset + (contentTimeframe.custom?.start || '')}>
                    <Pie
                      data={(contentUploads.by_solution_area || []).map(d => ({ name: d._id || 'Unknown', value: d.count }))}
                      dataKey="value"
                      nameKey="name"
                      cx="50%"
                      cy="50%"
                      outerRadius={90}
                      label={({ name, value, percent }) => `${name}: ${value} (${(percent * 100).toFixed(0)}%)`}
                      labelLine={true}
                    >
                      {(contentUploads.by_solution_area || []).map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip formatter={(value, name) => [`${value} assets`, name]} />
                  </PieChart>
                </ResponsiveContainer>
              </div>
              
              <div style={{ backgroundColor: 'white', borderRadius: '8px', padding: '24px', boxShadow: '0 1px 3px rgba(0,0,0,0.06)' }}>
                <h3 style={{ fontSize: '16px', fontWeight: '600', color: '#2C3E50', marginBottom: '16px' }}>Content by Type</h3>
                <ResponsiveContainer width="100%" height={280}>
                  <BarChart data={(contentUploads.by_content_type || []).map(d => ({ name: (d.id || d._id || 'Unknown').replace('_', ' '), assets: d.count || 0, secondary: (d._id || d.id) === 'Web URL' ? (d.total_clicks || 0) : (d.total_downloads || 0) }))}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#E0E0E0" />
                    <XAxis dataKey="name" tick={{ fontSize: 11 }} label={{ value: 'Content Type', position: 'insideBottom', offset: -5, fontSize: 12, fill: '#5A6C7D' }} />
                    <YAxis tick={{ fontSize: 12 }} label={{ value: 'Count', angle: -90, position: 'insideLeft', fontSize: 12, fill: '#5A6C7D' }} />
                    <Tooltip content={<ContentTypeTooltip />} />
                    <Legend wrapperStyle={{ paddingTop: '10px' }} />
                    <Bar dataKey="assets" fill="#1B5E5F" name="Assets" radius={[4, 4, 0, 0]} />
                    <Bar dataKey="secondary" fill="#0C6A1D" name="Downloads / Clicks" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>

            {/* Content by Language */}
            <div style={{ backgroundColor: 'white', borderRadius: '8px', padding: '24px', boxShadow: '0 1px 3px rgba(0,0,0,0.06)' }}>
              <h3 style={{ fontSize: '16px', fontWeight: '600', color: '#2C3E50', marginBottom: '16px' }}>Content by Language</h3>
              <ResponsiveContainer width="100%" height={220}>
                <BarChart data={(contentUploads.by_language || []).map(d => ({ name: d.id || d._id || 'Unknown', assets: d.count || 0, downloads: d.total_downloads || 0 }))} layout="vertical">
                  <CartesianGrid strokeDasharray="3 3" stroke="#E0E0E0" />
                  <XAxis type="number" tick={{ fontSize: 12 }} label={{ value: 'Count', position: 'insideBottom', offset: -5, fontSize: 12, fill: '#5A6C7D' }} />
                  <YAxis dataKey="name" type="category" tick={{ fontSize: 12 }} width={100} label={{ value: 'Language', angle: -90, position: 'insideLeft', fontSize: 12, fill: '#5A6C7D', dx: -20 }} />
                  <Tooltip formatter={(value, name) => [value, name === 'Assets' ? 'Total Assets' : 'Total Downloads/Clicks']} />
                  <Legend wrapperStyle={{ paddingTop: '10px' }} />
                  <Bar dataKey="assets" fill="#1B5E5F" name="Assets" radius={[0, 4, 4, 0]} />
                  <Bar dataKey="downloads" fill="#0C6A1D" name="Downloads/Clicks" radius={[0, 4, 4, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>

            {/* Content by Conversation (FY27 attribute, independent of Solution Area) */}
            <div style={{ backgroundColor: 'white', borderRadius: '8px', padding: '24px', boxShadow: '0 1px 3px rgba(0,0,0,0.06)' }}>
              <h3 style={{ fontSize: '16px', fontWeight: '600', color: '#2C3E50', marginBottom: '16px' }}>Content by Conversation</h3>
              {(contentUploads.by_conversation || []).length === 0 ? (
                <p style={{ fontSize: '13px', color: '#95A5A6', padding: '16px 0' }}>No Conversation-tagged content yet for this period.</p>
              ) : (
                <ResponsiveContainer width="100%" height={220}>
                  <BarChart data={(contentUploads.by_conversation || []).map(d => ({ name: d.id || d._id || 'Unknown', assets: d.count || 0, downloads: d.total_downloads || 0 }))} layout="vertical">
                    <CartesianGrid strokeDasharray="3 3" stroke="#E0E0E0" />
                    <XAxis type="number" tick={{ fontSize: 12 }} label={{ value: 'Count', position: 'insideBottom', offset: -5, fontSize: 12, fill: '#5A6C7D' }} />
                    <YAxis dataKey="name" type="category" tick={{ fontSize: 12 }} width={180} label={{ value: 'Conversation', angle: -90, position: 'insideLeft', fontSize: 12, fill: '#5A6C7D', dx: -20 }} />
                    <Tooltip formatter={(value, name) => [value, name === 'Assets' ? 'Total Assets' : 'Total Downloads/Clicks']} />
                    <Legend wrapperStyle={{ paddingTop: '10px' }} />
                    <Bar dataKey="assets" fill="#1B5E5F" name="Assets" radius={[0, 4, 4, 0]} />
                    <Bar dataKey="downloads" fill="#0C6A1D" name="Downloads/Clicks" radius={[0, 4, 4, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              )}
            </div>
            </div>
          </div>
        )}

        {/* Downloads Tab */}
        {activeTab === 'downloads' && (
          <div>
            <TimeframeFilter 
              currentTimeframe={downloadsTimeframe}
              onTimeframeChange={handleTimeframeChange}
              tabId="downloads"
              useCalendarRange={useCalendarRange}
            />
            <div style={{ display: 'grid', gap: '24px' }}>
            {/* Downloads by Solution Area and Play */}
            <div style={{ backgroundColor: 'white', borderRadius: '8px', padding: '24px', boxShadow: '0 1px 3px rgba(0,0,0,0.06)' }}>
              <h3 style={{ fontSize: '18px', fontWeight: '600', color: '#2C3E50', marginBottom: '16px' }}>Downloads by Solution Area & Conversation</h3>
              <table style={{ width: '100%', borderCollapse: 'collapse', tableLayout: 'fixed' }}>
                <thead style={{ backgroundColor: '#F5F5F5' }}>
                  <tr>
                    <th style={{ padding: '12px 16px', textAlign: 'left', fontSize: '12px', fontWeight: '600', color: '#5A6C7D', width: '20%' }}>Solution Area</th>
                    <th style={{ padding: '12px 16px', textAlign: 'left', fontSize: '12px', fontWeight: '600', color: '#5A6C7D', width: '50%' }}>Conversation</th>
                    <th style={{ padding: '12px 16px', textAlign: 'right', fontSize: '12px', fontWeight: '600', color: '#5A6C7D', width: '15%' }}>Downloads</th>
                    <th style={{ padding: '12px 16px', textAlign: 'right', fontSize: '12px', fontWeight: '600', color: '#5A6C7D', width: '15%' }}>Unique Users</th>
                  </tr>
                </thead>
                <tbody>
                  {downloadsBySolution.length === 0 ? (
                    <tr><td colSpan="4" style={{ padding: '20px', textAlign: 'center', color: '#9CA3AF' }}>No download data available</td></tr>
                  ) : (
                    downloadsBySolution.map((row, idx) => (
                      <tr key={idx} style={{ borderTop: '1px solid #E0E0E0' }}>
                        <td style={{ padding: '12px 16px', fontSize: '14px', fontWeight: '500', color: '#2C3E50' }}>{row.solution_area || 'Unknown'}</td>
                        <td style={{ padding: '12px 16px', fontSize: '14px', color: '#5A6C7D' }}>{row.conversations || '-'}</td>
                        <td style={{ padding: '12px 16px', fontSize: '14px', fontWeight: '600', color: '#1B5E5F', textAlign: 'right' }}>{row.download_count}</td>
                        <td style={{ padding: '12px 16px', fontSize: '14px', color: '#5A6C7D', textAlign: 'right' }}>{row.unique_user_count}</td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>

            {/* Click Rate by Solution Area & Play */}
            <div style={{ backgroundColor: 'white', borderRadius: '8px', padding: '24px', boxShadow: '0 1px 3px rgba(0,0,0,0.06)' }}>
              <h3 style={{ fontSize: '18px', fontWeight: '600', color: '#2C3E50', marginBottom: '16px' }}>
                Click Rate by Solution Area & Conversation
              </h3>
              <table style={{ width: '100%', borderCollapse: 'collapse', tableLayout: 'fixed' }}>
                <thead style={{ backgroundColor: '#F5F5F5' }}>
                  <tr>
                    <th style={{ padding: '12px 16px', textAlign: 'left', fontSize: '12px', fontWeight: '600', color: '#5A6C7D', width: '20%' }}>Solution Area</th>
                    <th style={{ padding: '12px 16px', textAlign: 'left', fontSize: '12px', fontWeight: '600', color: '#5A6C7D', width: '50%' }}>Conversation</th>
                    <th style={{ padding: '12px 16px', textAlign: 'right', fontSize: '12px', fontWeight: '600', color: '#5A6C7D', width: '15%' }}>Total Clicks</th>
                    <th style={{ padding: '12px 16px', textAlign: 'right', fontSize: '12px', fontWeight: '600', color: '#5A6C7D', width: '15%' }}>Unique Users</th>
                  </tr>
                </thead>
                <tbody>
                  {downloadsBySolution.filter(r => r.weburl_clicks > 0).length === 0 ? (
                    <tr><td colSpan="4" style={{ padding: '20px', textAlign: 'center', color: '#9CA3AF' }}>No Web URL click data for this period</td></tr>
                  ) : (
                    downloadsBySolution
                      .filter(r => r.weburl_clicks > 0)
                      .sort((a, b) => b.weburl_clicks - a.weburl_clicks)
                      .map((row, idx) => (
                        <tr key={idx} style={{ borderTop: '1px solid #E0E0E0' }}>
                          <td style={{ padding: '12px 16px', fontSize: '14px', fontWeight: '500', color: '#2C3E50' }}>{row.solution_area || 'Unknown'}</td>
                          <td style={{ padding: '12px 16px', fontSize: '14px', color: '#5A6C7D' }}>{row.conversations || '-'}</td>
                          <td style={{ padding: '12px 16px', fontSize: '14px', fontWeight: '600', color: '#1B5E5F', textAlign: 'right' }}>{row.weburl_clicks}</td>
                          <td style={{ padding: '12px 16px', fontSize: '14px', color: '#5A6C7D', textAlign: 'right' }}>{row.weburl_unique_users}</td>
                        </tr>
                      ))
                  )}
                </tbody>
              </table>
            </div>

            {/* Downloads by Country */}
            <div style={{ backgroundColor: 'white', borderRadius: '8px', padding: '24px', boxShadow: '0 1px 3px rgba(0,0,0,0.06)' }}>
              <h3 style={{ fontSize: '18px', fontWeight: '600', color: '#2C3E50', marginBottom: '16px' }}>Downloads by Country</h3>
              <ResponsiveContainer width="100%" height={320}>
                <BarChart data={countryData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E0E0E0" />
                  <XAxis dataKey="name" tick={{ fontSize: 11 }} label={{ value: 'Country', position: 'insideBottom', offset: -5, fontSize: 12, fill: '#5A6C7D' }} />
                  <YAxis tick={{ fontSize: 12 }} label={{ value: 'Downloads', angle: -90, position: 'insideLeft', fontSize: 12, fill: '#5A6C7D' }} />
                  <Tooltip formatter={(value) => [value, 'Downloads']} />
                  <Legend wrapperStyle={{ paddingTop: '10px' }} />
                  <Bar dataKey="downloads" fill="#2A7A7B" name="Downloads" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
            </div>
          </div>
        )}

        {/* Partner Analytics Tab */}
        {activeTab === 'partners' && (
          <div>
            <TimeframeFilter 
              currentTimeframe={partnersTimeframe}
              onTimeframeChange={handleTimeframeChange}
              tabId="partners"
              useCalendarRange={useCalendarRange}
            />
            <div style={{ display: 'grid', gap: '24px' }}>
            {/* Top Partner Domains */}
            <div style={{ backgroundColor: 'white', borderRadius: '8px', padding: '24px', boxShadow: '0 1px 3px rgba(0,0,0,0.06)' }}>
              <h3 style={{ fontSize: '18px', fontWeight: '600', color: '#2C3E50', marginBottom: '16px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                <Building2 size={20} color="#1B5E5F" />
                Downloads by Partner Organization
              </h3>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead style={{ backgroundColor: '#F5F5F5' }}>
                  <tr>
                    <th style={{ padding: '12px 16px', textAlign: 'left', fontSize: '12px', fontWeight: '600', color: '#5A6C7D' }}>Organization Domain</th>
                    <th style={{ padding: '12px 16px', textAlign: 'right', fontSize: '12px', fontWeight: '600', color: '#5A6C7D' }}>Total Downloads</th>
                    <th style={{ padding: '12px 16px', textAlign: 'right', fontSize: '12px', fontWeight: '600', color: '#5A6C7D' }}>Unique Users</th>
                  </tr>
                </thead>
                <tbody>
                  {downloadsByPartner.length === 0 ? (
                    <tr><td colSpan="3" style={{ padding: '20px', textAlign: 'center', color: '#9CA3AF' }}>No partner data available</td></tr>
                  ) : (
                    downloadsByPartner.map((row, idx) => (
                      <tr key={idx} style={{ borderTop: '1px solid #E0E0E0' }}>
                        <td style={{ padding: '12px 16px', fontSize: '14px', fontWeight: '500', color: '#2C3E50' }}>{row.domain || 'Unknown'}</td>
                        <td style={{ padding: '12px 16px', fontSize: '14px', fontWeight: '600', color: '#1B5E5F', textAlign: 'right' }}>{row.download_count || 0}</td>
                        <td style={{ padding: '12px 16px', fontSize: '14px', color: '#5A6C7D', textAlign: 'right' }}>{row.unique_user_count || 0}</td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>

            {/* Click Rate by Partner Organization */}
            <div style={{ backgroundColor: 'white', borderRadius: '8px', padding: '24px', boxShadow: '0 1px 3px rgba(0,0,0,0.06)' }}>
              <h3 style={{ fontSize: '18px', fontWeight: '600', color: '#2C3E50', marginBottom: '16px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                <MousePointerClick size={18} color="#1B5E5F" />
                Click Rate by Partner Organization
              </h3>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead style={{ backgroundColor: '#F5F5F5' }}>
                  <tr>
                    <th style={{ padding: '12px 16px', textAlign: 'left', fontSize: '12px', fontWeight: '600', color: '#5A6C7D' }}>Organization Domain</th>
                    <th style={{ padding: '12px 16px', textAlign: 'right', fontSize: '12px', fontWeight: '600', color: '#5A6C7D' }}>Total Clicks</th>
                    <th style={{ padding: '12px 16px', textAlign: 'right', fontSize: '12px', fontWeight: '600', color: '#5A6C7D' }}>Unique Users</th>
                  </tr>
                </thead>
                <tbody>
                  {downloadsByPartner.filter(r => r.weburl_clicks > 0).length === 0 ? (
                    <tr><td colSpan="3" style={{ padding: '20px', textAlign: 'center', color: '#9CA3AF' }}>No Web URL click data for this period</td></tr>
                  ) : (
                    downloadsByPartner
                      .filter(r => r.weburl_clicks > 0)
                      .sort((a, b) => b.weburl_clicks - a.weburl_clicks)
                      .map((row, idx) => (
                        <tr key={idx} style={{ borderTop: '1px solid #E0E0E0' }}>
                          <td style={{ padding: '12px 16px', fontSize: '14px', fontWeight: '500', color: '#2C3E50' }}>{row.domain || 'Unknown'}</td>
                          <td style={{ padding: '12px 16px', fontSize: '14px', fontWeight: '600', color: '#1B5E5F', textAlign: 'right' }}>{row.weburl_clicks}</td>
                          <td style={{ padding: '12px 16px', fontSize: '14px', color: '#5A6C7D', textAlign: 'right' }}>{row.weburl_unique_users}</td>
                        </tr>
                      ))
                  )}
                </tbody>
              </table>
            </div>

            {/* Downloads by Domain Chart */}
            <div style={{ backgroundColor: 'white', borderRadius: '8px', padding: '24px', boxShadow: '0 1px 3px rgba(0,0,0,0.06)' }}>
              <h3 style={{ fontSize: '18px', fontWeight: '600', color: '#2C3E50', marginBottom: '16px' }}>Top Partner Domains Visualization</h3>
              <ResponsiveContainer width="100%" height={320}>
                <BarChart data={downloadsByPartner.slice(0, 10).map(d => ({ name: d.domain, downloads: d.downloads || 0, clicks: d.weburl_clicks || 0 }))} layout="vertical">
                  <CartesianGrid strokeDasharray="3 3" stroke="#E0E0E0" />
                  <XAxis type="number" tick={{ fontSize: 12 }} label={{ value: 'Count', position: 'insideBottom', offset: -5, fontSize: 12, fill: '#5A6C7D' }} />
                  <YAxis dataKey="name" type="category" tick={{ fontSize: 11 }} width={120} />
                  <Tooltip formatter={(value, name) => [value, name === 'Downloads' ? 'Total Downloads' : 'Total Clicks (Web URL)']} />
                  <Legend wrapperStyle={{ paddingTop: '10px' }} />
                  <Bar dataKey="downloads" fill="#1B5E5F" name="Downloads" radius={[0, 4, 4, 0]} />
                  <Bar dataKey="clicks" fill="#0891B2" name="Clicks" radius={[0, 4, 4, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
            </div>
          </div>
        )}


        {/* Feedback Analytics Tab */}
        {activeTab === 'feedback' && feedbackAnalytics && (
          <div>
            <TimeframeFilter 
              currentTimeframe={feedbackTimeframe}
              onTimeframeChange={handleTimeframeChange}
              tabId="feedback"
              useCalendarRange={useCalendarRange}
            />
            <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
            {/* Summary Cards */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '16px' }}>
              <div style={{ backgroundColor: 'white', borderRadius: '8px', padding: '20px', boxShadow: '0 1px 3px rgba(0,0,0,0.06)' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '12px' }}>
                  <div style={{ backgroundColor: '#E8F5E9', padding: '10px', borderRadius: '8px' }}><MessageSquare size={20} color="#1B5E5F" /></div>
                  <span style={{ color: '#5A6C7D', fontSize: '14px' }}>Total Feedback</span>
                </div>
                <div style={{ fontSize: '32px', fontWeight: '700', color: '#2C3E50' }}>{feedbackAnalytics.total}</div>
              </div>
              <div style={{ backgroundColor: 'white', borderRadius: '8px', padding: '20px', boxShadow: '0 1px 3px rgba(0,0,0,0.06)' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '12px' }}>
                  <div style={{ backgroundColor: '#FFF3E0', padding: '10px', borderRadius: '8px' }}><Star size={20} color="#F59E0B" /></div>
                  <span style={{ color: '#5A6C7D', fontSize: '14px' }}>Avg Rating</span>
                </div>
                <div style={{ fontSize: '32px', fontWeight: '700', color: '#F59E0B' }}>{feedbackAnalytics.avg_rating}<span style={{ fontSize: '16px', color: '#9CA3AF' }}>/5</span></div>
                <div style={{ fontSize: '12px', color: '#9CA3AF', marginTop: '4px' }}>{feedbackAnalytics.rated_count} rated</div>
              </div>
              {feedbackAnalytics.by_status.map(s => (
                <div key={s.status} style={{ backgroundColor: 'white', borderRadius: '8px', padding: '20px', boxShadow: '0 1px 3px rgba(0,0,0,0.06)' }}>
                  <div style={{ fontSize: '13px', color: '#5A6C7D', textTransform: 'capitalize', marginBottom: '12px' }}>{s.status}</div>
                  <div style={{ fontSize: '32px', fontWeight: '700', color: s.status === 'new' ? '#1E40AF' : s.status === 'reviewed' ? '#92400E' : '#065F46' }}>{s.count}</div>
                </div>
              ))}
            </div>

            {/* Charts Row */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '24px' }}>
              {/* By Type */}
              <div style={{ backgroundColor: 'white', borderRadius: '8px', padding: '24px', boxShadow: '0 1px 3px rgba(0,0,0,0.06)' }}>
                <h3 style={{ fontSize: '16px', fontWeight: '600', color: '#2C3E50', marginBottom: '16px' }}>Feedback by Type</h3>
                <ResponsiveContainer width="100%" height={280}>
                  <PieChart>
                    <Pie data={feedbackAnalytics.by_type.map(d => ({ name: d.type, value: d.count }))} cx="50%" cy="50%" outerRadius={90} fill="#1B5E5F" dataKey="value" label={({ name, value }) => `${name} (${value})`}>
                      {feedbackAnalytics.by_type.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                    </Pie>
                    <Tooltip />
                  </PieChart>
                </ResponsiveContainer>
              </div>

              {/* Rating Distribution */}
              <div style={{ backgroundColor: 'white', borderRadius: '8px', padding: '24px', boxShadow: '0 1px 3px rgba(0,0,0,0.06)' }}>
                <h3 style={{ fontSize: '16px', fontWeight: '600', color: '#2C3E50', marginBottom: '16px' }}>Rating Distribution</h3>
                <ResponsiveContainer width="100%" height={280}>
                  <BarChart data={feedbackAnalytics.rating_distribution.map(d => ({ rating: `${d.rating} Star${d.rating > 1 ? 's' : ''}`, count: d.count }))}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
                    <XAxis dataKey="rating" tick={{ fontSize: 12 }} />
                    <YAxis tick={{ fontSize: 12 }} allowDecimals={false} />
                    <Tooltip />
                    <Bar dataKey="count" fill="#F59E0B" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>

            {/* Trend Chart */}
            {feedbackAnalytics.recent_trend.length > 1 && (
              <div style={{ backgroundColor: 'white', borderRadius: '8px', padding: '24px', boxShadow: '0 1px 3px rgba(0,0,0,0.06)' }}>
                <h3 style={{ fontSize: '16px', fontWeight: '600', color: '#2C3E50', marginBottom: '16px' }}>Feedback Trend ({getTimeframeLabel(feedbackTimeframe)})</h3>
                <ResponsiveContainer width="100%" height={250}>
                  <AreaChart data={feedbackAnalytics.recent_trend}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
                    <XAxis dataKey="date" tick={{ fontSize: 11 }} />
                    <YAxis tick={{ fontSize: 12 }} allowDecimals={false} />
                    <Tooltip />
                    <Area type="monotone" dataKey="count" stroke="#1B5E5F" fill="#E8F5E9" strokeWidth={2} />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            )}

            {/* Content Feedback Table */}
            {feedbackAnalytics.content_feedback && feedbackAnalytics.content_feedback.length > 0 && (
              <div style={{ backgroundColor: 'white', borderRadius: '8px', padding: '24px', boxShadow: '0 1px 3px rgba(0,0,0,0.06)' }}>
                <h3 style={{ fontSize: '16px', fontWeight: '600', color: '#2C3E50', marginBottom: '16px' }}>Content Feedback ({feedbackAnalytics.content_feedback.length})</h3>
                <p style={{ fontSize: '13px', color: '#9CA3AF', marginBottom: '16px' }}>User feedback specifically about portal content quality and requests.</p>
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead style={{ backgroundColor: '#F9FAFB' }}>
                    <tr>
                      <th style={{ padding: '10px 16px', textAlign: 'left', fontSize: '12px', fontWeight: '600', color: '#5A6C7D', textTransform: 'uppercase' }}>Message</th>
                      <th style={{ padding: '10px 16px', textAlign: 'center', fontSize: '12px', fontWeight: '600', color: '#5A6C7D', textTransform: 'uppercase', width: '100px' }}>Rating</th>
                      <th style={{ padding: '10px 16px', textAlign: 'right', fontSize: '12px', fontWeight: '600', color: '#5A6C7D', textTransform: 'uppercase', width: '120px' }}>Date</th>
                    </tr>
                  </thead>
                  <tbody>
                    {feedbackAnalytics.content_feedback.map((fb, idx) => (
                      <tr key={idx} style={{ borderTop: '1px solid #E5E7EB' }}>
                        <td style={{ padding: '12px 16px', fontSize: '14px', color: '#374151', lineHeight: '1.5' }}>{fb.message}</td>
                        <td style={{ padding: '12px 16px', textAlign: 'center' }}>
                          {fb.rating ? (
                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '2px' }}>
                              {[1,2,3,4,5].map(s => (
                                <Star key={s} size={12} fill={s <= fb.rating ? '#F59E0B' : 'none'} color={s <= fb.rating ? '#F59E0B' : '#D1D5DB'} />
                              ))}
                            </div>
                          ) : <span style={{ color: '#9CA3AF' }}>—</span>}
                        </td>
                        <td style={{ padding: '12px 16px', fontSize: '13px', color: '#9CA3AF', textAlign: 'right', whiteSpace: 'nowrap' }}>{fb.date}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            </div>
          </div>
        )}

        {/* NDA Tracking Tab - Admin Only */}
        {activeTab === 'nda' && isAdmin && (
          <div>
            <TimeframeFilter 
              currentTimeframe={ndaTimeframe}
              onTimeframeChange={handleTimeframeChange}
              tabId="nda"
              useCalendarRange={useCalendarRange}
            />
            <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
            {/* Summary Cards */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '16px' }}>
              <div style={{ backgroundColor: 'white', borderRadius: '8px', padding: '20px', boxShadow: '0 1px 3px rgba(0,0,0,0.06)' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '8px' }}>
                  <Shield size={24} color="#0C6A1D" />
                  <span style={{ fontSize: '14px', color: '#5A6C7D' }}>Total NDA Acceptances</span>
                </div>
                <span style={{ fontSize: '32px', fontWeight: '700', color: '#2C3E50' }}>{ndaSummary.accepted || 0}</span>
              </div>
              <div style={{ backgroundColor: 'white', borderRadius: '8px', padding: '20px', boxShadow: '0 1px 3px rgba(0,0,0,0.06)' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '8px' }}>
                  <Users size={24} color="#1B5E5F" />
                  <span style={{ fontSize: '14px', color: '#5A6C7D' }}>Total Users</span>
                </div>
                <span style={{ fontSize: '32px', fontWeight: '700', color: '#2C3E50' }}>{ndaSummary.total || 0}</span>
              </div>
              <div style={{ backgroundColor: 'white', borderRadius: '8px', padding: '20px', boxShadow: '0 1px 3px rgba(0,0,0,0.06)' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '8px' }}>
                  <FileCheck size={24} color="#2A7A7B" />
                  <span style={{ fontSize: '14px', color: '#5A6C7D' }}>Compliance Rate</span>
                </div>
                <span style={{ fontSize: '32px', fontWeight: '700', color: '#2C3E50' }}>
                  {ndaSummary.acceptance_rate || 0}%
                </span>
              </div>
            </div>

            {/* NDA by Domain Summary */}
            <div style={{ backgroundColor: 'white', borderRadius: '8px', padding: '24px', boxShadow: '0 1px 3px rgba(0,0,0,0.06)' }}>
              <h3 style={{ fontSize: '18px', fontWeight: '600', color: '#2C3E50', marginBottom: '16px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                <Building2 size={20} color="#1B5E5F" />
                NDA Acceptances by Organization Domain
              </h3>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead style={{ backgroundColor: '#F5F5F5' }}>
                  <tr>
                    <th style={{ padding: '12px 16px', textAlign: 'left', fontSize: '12px', fontWeight: '600', color: '#5A6C7D' }}>Domain</th>
                    <th style={{ padding: '12px 16px', textAlign: 'right', fontSize: '12px', fontWeight: '600', color: '#5A6C7D' }}>Acceptances</th>
                  </tr>
                </thead>
                <tbody>
                  {(ndaSummary.by_domain || []).length === 0 ? (
                    <tr><td colSpan="2" style={{ padding: '20px', textAlign: 'center', color: '#95A5A6' }}>No data available</td></tr>
                  ) : (
                    (ndaSummary.by_domain || []).map((item, idx) => (
                      <tr key={idx} style={{ borderTop: '1px solid #E0E0E0' }}>
                        <td style={{ padding: '12px 16px', fontSize: '14px', color: '#2C3E50' }}>{item._id || 'Unknown'}</td>
                        <td style={{ padding: '12px 16px', fontSize: '14px', color: '#5A6C7D', textAlign: 'right', fontWeight: '600' }}>{item.count}</td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>

            {/* User NDA Lookup */}
            <div style={{ backgroundColor: 'white', borderRadius: '8px', padding: '24px', boxShadow: '0 1px 3px rgba(0,0,0,0.06)' }}>
              <h3 style={{ fontSize: '18px', fontWeight: '600', color: '#2C3E50', marginBottom: '16px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                <Search size={20} color="#1B5E5F" />
                Lookup User NDA Status
              </h3>
              <p style={{ fontSize: '14px', color: '#5A6C7D', marginBottom: '16px' }}>
                Search for a specific user by email to view their NDA acceptance details.
              </p>
              <div style={{ display: 'flex', gap: '12px', marginBottom: '16px' }}>
                <input
                  type="email"
                  placeholder="Enter user email address"
                  value={ndaLookupEmail}
                  onChange={(e) => setNdaLookupEmail(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && lookupNdaByEmail()}
                  style={{
                    flex: 1,
                    padding: '12px 16px',
                    border: '1px solid #E0E0E0',
                    borderRadius: '8px',
                    fontSize: '14px'
                  }}
                  data-testid="nda-lookup-email"
                />
                <button
                  onClick={lookupNdaByEmail}
                  disabled={ndaLookupLoading}
                  style={{
                    padding: '12px 24px',
                    backgroundColor: '#1B5E5F',
                    color: 'white',
                    border: 'none',
                    borderRadius: '8px',
                    fontWeight: '600',
                    cursor: ndaLookupLoading ? 'not-allowed' : 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px'
                  }}
                  data-testid="nda-lookup-btn"
                >
                  <Search size={16} />
                  {ndaLookupLoading ? 'Searching...' : 'Lookup'}
                </button>
              </div>

              {ndaLookupError && (
                <div style={{ padding: '12px 16px', backgroundColor: '#FFF3F3', border: '1px solid #FFCDD2', borderRadius: '8px', color: '#C62828', fontSize: '14px', marginBottom: '16px' }}>
                  {ndaLookupError}
                </div>
              )}

              {ndaLookupResult && (
                <div style={{ padding: '20px', backgroundColor: '#F5F5F5', borderRadius: '8px' }}>
                  <h4 style={{ fontSize: '16px', fontWeight: '600', color: '#2C3E50', marginBottom: '16px' }}>User NDA Details</h4>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '16px' }}>
                    <div>
                      <span style={{ fontSize: '12px', color: '#5A6C7D', textTransform: 'uppercase' }}>Name</span>
                      <p style={{ fontSize: '14px', fontWeight: '500', color: '#2C3E50', marginTop: '4px' }}>{ndaLookupResult.name || '-'}</p>
                    </div>
                    <div>
                      <span style={{ fontSize: '12px', color: '#5A6C7D', textTransform: 'uppercase' }}>Email</span>
                      <p style={{ fontSize: '14px', fontWeight: '500', color: '#2C3E50', marginTop: '4px' }}>{ndaLookupResult.email || '-'}</p>
                    </div>
                    <div>
                      <span style={{ fontSize: '12px', color: '#5A6C7D', textTransform: 'uppercase' }}>Organization</span>
                      <p style={{ fontSize: '14px', fontWeight: '500', color: '#2C3E50', marginTop: '4px' }}>{ndaLookupResult.org_domain || '-'}</p>
                    </div>
                    <div>
                      <span style={{ fontSize: '12px', color: '#5A6C7D', textTransform: 'uppercase' }}>NDA Status</span>
                      <p style={{ fontSize: '14px', fontWeight: '600', marginTop: '4px', color: ndaLookupResult.nda_accepted ? '#0C6A1D' : '#C62828' }}>
                        {ndaLookupResult.nda_accepted ? '✓ Accepted' : '✗ Not Accepted'}
                      </p>
                    </div>
                    {ndaLookupResult.nda_accepted && (
                      <>
                        <div>
                          <span style={{ fontSize: '12px', color: '#5A6C7D', textTransform: 'uppercase' }}>NDA Version</span>
                          <p style={{ fontSize: '14px', fontWeight: '500', color: '#2C3E50', marginTop: '4px' }}>{ndaLookupResult.nda_accepted_version || 'v1.0'}</p>
                        </div>
                        <div>
                          <span style={{ fontSize: '12px', color: '#5A6C7D', textTransform: 'uppercase' }}>Accepted At</span>
                          <p style={{ fontSize: '14px', fontWeight: '500', color: '#2C3E50', marginTop: '4px' }}>
                            {ndaLookupResult.nda_accepted_at ? new Date(ndaLookupResult.nda_accepted_at).toLocaleString() : '-'}
                          </p>
                        </div>
                      </>
                    )}
                  </div>
                </div>
              )}
            </div>
            </div>
          </div>
        )}
      </div>
      </div>

      <LevelUpFooter />
    </div>
  );
}

export default Analytics;
